import React from 'react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { apiFetch } from '../../lib/api-client'

interface ExportList {
  key: string
  title: string
  description: string
  available: boolean
  fetchUrl?: string
  columns?: string[]
}

const EXPORT_LISTS: ExportList[] = [
  {
    key: 'renters',
    title: 'Lejeroversigt',
    description: 'Alle lejere med navn, email, telefon og status.',
    available: true,
    fetchUrl: '/api/v1/renters',
    columns: ['Navn', 'E-mail', 'Telefon', 'Status', 'Lejertype'],
  },
  {
    key: 'leases',
    title: 'Lejemålsoversigt',
    description: 'Alle lejemål med adresse, type, areal, status og løbenummer.',
    available: true,
    fetchUrl: '/api/v1/leases',
    columns: ['Løbenr.', 'Adresse', 'Enhed', 'Type', 'Areal (m²)', 'Rum', 'Status'],
  },
  {
    key: 'contracts',
    title: 'Lejeforholdsoversigt',
    description: 'Alle lejeforhold med lejemål, månedsleje, indflytning og status.',
    available: true,
    fetchUrl: '/api/v1/contracts',
    columns: ['Løbenr.', 'Lejemål', 'Type', 'Månedsleje', 'Indflytning', 'Status'],
  },
  {
    key: 'deposit',
    title: 'Depositum og forudbetalt leje',
    description: 'Depositum + forudbetalt leje per aktivt lejeforhold.',
    available: true,
    fetchUrl: '/api/v1/exports/depositum',
    columns: ['Løbenr.', 'Lejer', 'Adresse', 'Depositum', 'Forudbetalt leje', 'Indflytning'],
  },
  {
    key: 'aconto',
    title: 'Acontobalancer',
    description: 'Aconto opkrævet vs. faktisk forbrug per forbrugsregnskab — difference og udfald.',
    available: true,
    fetchUrl: '/api/v1/exports/aconto',
    columns: ['Løbenr.', 'Lejer', 'Adresse', 'Regnskabsår', 'Type', 'Aconto opkrævet', 'Faktisk (lejers andel)', 'Difference', 'Udfald', 'Status'],
  },
  {
    key: 'receivable',
    title: 'Restance / tilgodehavende',
    description: 'Reelt udestående per lejemål — debitorsaldo fra bogføringen.',
    available: true,
    fetchUrl: '/api/v1/exports/restance',
    columns: ['Løbenr.', 'Lejer', 'Adresse', 'Enhed', 'Restance'],
  },
  {
    key: 'idle',
    title: 'Tomgangsoversigt',
    description: 'Ledige lejemål med adresse, areal og værelser.',
    available: true,
    fetchUrl: '/api/v1/exports/tomgang',
    columns: ['Løbenr.', 'Ejendom', 'Adresse', 'Enhed', 'Areal (m²)', 'Værelser', 'Status'],
  },
  {
    key: 'balances',
    title: 'Saldobalance',
    description: 'Alle konti med debet, kredit og saldo fra bogføringen.',
    available: true,
    fetchUrl: '/api/v1/exports/saldobalance',
    columns: ['Kontonr.', 'Kontonavn', 'Type', 'Debet', 'Kredit', 'Saldo'],
  },
  {
    key: 'moves',
    title: 'Flytninger',
    description: 'Ind- og udflytninger med lejer, datoer og status.',
    available: true,
    fetchUrl: '/api/v1/exports/flytninger',
    columns: ['Løbenr.', 'Lejer', 'Adresse', 'Status', 'Indflytning', 'Fraflytning'],
  },
]

function objectsToCsv(rows: Record<string, unknown>[]): string {
  const keys = Object.keys(rows[0] ?? {})
  const header = keys.join(';')
  const body = rows.map((r) => keys.map((k) => String(r[k] ?? '')).join(';')).join('\n')
  return `${header}\n${body}`
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function ExportCard({ item }: { item: ExportList }) {
  const [loading, setLoading] = React.useState(false)
  const [hovered, setHovered] = React.useState(false)

  const handleDownload = async () => {
    if (!item.fetchUrl || !item.available) return
    setLoading(true)
    try {
      const data = await apiFetch<unknown>(item.fetchUrl + '?limit=500')
      let rows: Record<string, unknown>[] = []
      if (Array.isArray(data)) {
        rows = data as Record<string, unknown>[]
      } else if (data && typeof data === 'object' && 'items' in data) {
        rows = (data as { items: Record<string, unknown>[] }).items
      }
      if (rows.length === 0) {
        alert('Ingen data at eksportere.')
        return
      }
      const csv = objectsToCsv(rows)
      downloadCsv(`${item.key}-${new Date().toISOString().slice(0, 10)}.csv`, csv)
    } catch {
      alert('Eksport fejlede. Prøv igen.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered && item.available ? '#f8fafc' : '#fff',
        border: '1px solid #e8ecf4',
        borderRadius: 10,
        padding: '18px 20px',
        transition: 'background 0.12s, box-shadow 0.12s',
        boxShadow: hovered && item.available ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        opacity: item.available ? 1 : 0.6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 4 }}>
            {item.title}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{item.description}</div>
          {item.columns && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Kolonner: {item.columns.slice(0, 4).join(', ')}{item.columns.length > 4 ? ` +${item.columns.length - 4}` : ''}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          {item.available ? (
            <button
              onClick={handleDownload}
              disabled={loading}
              style={{
                padding: '6px 14px',
                background: '#4f46e5',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'opacity 0.12s',
                fontFamily: 'inherit',
              }}
            >
              {loading ? 'Henter…' : 'Hent CSV'}
            </button>
          ) : (
            <span style={{
              padding: '6px 14px',
              background: '#f1f5f9',
              color: '#94a3b8',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
            }}>
              Kommer snart
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function ListerPage() {
  const [search, setSearch] = React.useState('')

  const filtered = EXPORT_LISTS.filter(
    (l) => l.title.toLowerCase().includes(search.toLowerCase()) ||
           l.description.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <AppLayout>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Lister</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Forudbyggede rapporter og eksporter. Åbn en liste for at filtrere og downloade som CSV.
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søg i lister…"
          style={{
            width: '100%',
            maxWidth: 360,
            padding: '8px 14px',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            fontSize: 13,
            color: '#0f172a',
            background: '#fff',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
        {filtered.map((item) => (
          <ExportCard key={item.key} item={item} />
        ))}
      </div>

      {filtered.length === 0 && (
        <Card>
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            Ingen lister matcher søgningen.
          </div>
        </Card>
      )}

      <div style={{ marginTop: 24, padding: '14px 18px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e8ecf4', fontSize: 12, color: '#64748b' }}>
        <strong>Savner du en bestemt type liste?</strong> Kontakt support.
        Eksport til Excel (XLSX) og PDF kommer i en kommende opdatering.
      </div>
    </AppLayout>
  )
}
